/**
 * PostHog wiring for the vendor-agnostic analytics seam (./analytics.ts).
 *
 * `track()` dispatches to `window.__analyticsSink`; this module sets that sink to
 * PostHog's `capture`. Call `initAnalytics()` once at app boot (see index.tsx).
 *
 * Key-gated: with no `REACT_APP_POSTHOG_KEY` it is a clean no-op (the sink stays
 * unset, so `track()` only console.debugs in dev). That keeps local dev and any
 * unconfigured environment free of network calls — wiring "turns on" purely by
 * setting the env var, no code change.
 *
 * Env:
 *   REACT_APP_POSTHOG_KEY   PostHog project API key (required to enable)
 *   REACT_APP_POSTHOG_HOST  ingest host (optional; default US cloud)
 */

import { flushPreInitEvents } from "./analytics";

let initialized = false;

/** localStorage key an in-app control can set to "1" to opt out of analytics. */
export const ANALYTICS_OPTOUT_KEY = "cf_analytics_optout";

/**
 * Honor the opt-out the privacy policy promises (section 2/4: legitimate interest
 * with the right to opt out): a stored flag, the Global Privacy Control signal,
 * or an explicit Do-Not-Track. When any is set, PostHog is never initialized — no
 * identifiers, no capture — making the stated opt-out actionable in-app, not only
 * via email (D-70).
 */
export function analyticsOptedOut(): boolean {
  try {
    if (localStorage.getItem(ANALYTICS_OPTOUT_KEY) === "1") return true;
  } catch {
    // localStorage can throw under some privacy modes — treat as no stored flag.
  }
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  if (nav.globalPrivacyControl === true) return true;
  const dnt = nav.doNotTrack ?? (window as Window & { doNotTrack?: string }).doNotTrack;
  return dnt === "1" || dnt === "yes";
}

export async function initAnalytics(): Promise<void> {
  if (initialized) return;
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug("[analytics] REACT_APP_POSTHOG_KEY not set — analytics disabled");
    }
    return;
  }
  if (analyticsOptedOut()) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug("[analytics] user opted out (stored flag / GPC / DNT) — analytics disabled");
    }
    return;
  }
  initialized = true;

  const host = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
  // Dynamic import so posthog-js isn't pulled into the initial chunk when a key
  // is present only in some environments, and never executed without a key.
  const { default: posthog } = await import("posthog-js");

  posthog.init(key, {
    api_host: host,
    // We emit explicit, dot-namespaced events via track() (see analytics.ts), so
    // disable PostHog's automatic capture to avoid noisy/duplicate events. The
    // owner can flip these on later for autocapture + SPA pageviews.
    capture_pageview: false,
    autocapture: false,
    persistence: "localStorage+cookie",
  });

  window.__analyticsSink = (event, props) => posthog.capture(event, props);
  window.__analyticsIdentify = (userId, traits) => posthog.identify(userId, traits);
  window.__analyticsReset = () => posthog.reset();

  // Replay any events fired before the sink was ready (e.g. page_view on mount,
  // which races ahead of this async import). Without this they are lost.
  flushPreInitEvents();
}
